// Copyright 2017 Sorama B.V.

import {
    autoinject,
    bindable,
    bindingMode,
    customElement,
    computedFrom,
    BindingEngine,
    Disposable,
} from "aurelia-framework";
import { IPagedRequest } from "libraries/WebRequestHandler/IWebRequestHandler";

@customElement("detailed-dropdown")
@autoinject
export class DetailedDropdown {
    //#region Bindable Properties
    @bindable
    public pagedRequest: IPagedRequest<unknown>;

    @bindable({ defaultValue: 10 })
    public remainingItemsLoadThreshold: number;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public filterText = "";

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public selectedItem: unknown;

    @bindable({ defaultBindingMode: bindingMode.oneWay })
    public namePropertyName = "Name";

    @bindable({ defaultBindingMode: bindingMode.oneWay })
    public linePropertyNames: string[] = [];
    //#endregion

    //#region Computed Properties
    @computedFrom("scrollContainer.scrollHeight", "scrollContainer.scrollTop", "scrollContainer.offsetHeight")
    protected get scrollPercentage(): number {
        if (this.scrollContainer.scrollHeight === 0) return 1;

        return (this.scrollContainer.scrollTop + this.scrollContainer.offsetHeight) / this.scrollContainer.scrollHeight;
    }

    //#endregion

    //#region Elements
    private scrollContainer: HTMLElement;
    //#endregion

    //#region Private Properties
    private loadPromise: Promise<void>;

    private scrollBinding: Disposable;
    //#endregion

    //#region Constructor and Aurelia Callbacks
    constructor(private readonly bindingEngine: BindingEngine) {}

    protected bind(): void {
        this.scrollBinding = this.bindingEngine
            .propertyObserver(this, "scrollPercentage")
            .subscribe(this.scrollPercentageChanged);

        this.pagedRequestChanged(this.pagedRequest);
    }

    protected unbind(): void {
        this.scrollBinding.dispose();
    }

    //#endregion

    //#region Callbacks

    protected pagedRequestChanged(newPagedRequest: IPagedRequest<unknown>): void {
        if (newPagedRequest) {
            // Start loading it, ignore any existing load promise
            this.loadNext();
        }
    }

    private scrollPercentageChanged = (newPercentage: number): void => {
        if (this.loadPromise || this.pagedRequest.finished) {
            // Already loading, ignore this update
            return;
        }

        if ((1 - newPercentage) * this.pagedRequest.total.length < this.remainingItemsLoadThreshold) {
            this.loadNext();
        }
    };

    //#endregion

    //#region Helper Methods

    private loadNext(): void {
        let loadPromise: Promise<void>;
        this.loadPromise = loadPromise = this.pagedRequest
            .next()
            .catch((e) => {
                console.error(e);
            })
            .then(() => {
                // If the current load promise is still this promise, unset it
                if (this.loadPromise == loadPromise) {
                    this.loadPromise = undefined;
                }
            });
    }

    protected clearSearch = (event: MouseEvent): void => {
        event.stopPropagation();
        this.filterText = "";
    };

    //#endregion
}
